import React, { useState, useEffect } from "react";
import { XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { ductSystemDropdownData } from "./ductSystemDropdown";

export default function DuctSystemModal({
  isOpen,
  onClose,
  onSubmit,
  editingSystem,
  onSaveAndContinue,
}) {
  const [systemTitle, setSystemTitle] = useState("");
  const [activeTab, setActiveTab] = useState(1);
  const [categoryIds, setCategoryIds] = useState([1]); // Track which categories exist
  const [nextCategoryId, setNextCategoryId] = useState(2); // For generating unique IDs
  const [categories, setCategories] = useState({
    1: {
      title: "",
      pressureClass: "",
      sealClass: "",
      wrap: "",
      liner: "",
      notes: "",
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (editingSystem && isOpen) {
      setSystemTitle(editingSystem.title || "");

      if (editingSystem.categories && editingSystem.categories.length > 0) {
        const categoryIds = editingSystem.categories.map(
          (cat, index) => index + 1
        );
        const categoriesData = editingSystem.categories.reduce(
          (acc, cat, index) => {
            acc[index + 1] = {
              title: cat.name || "",
              pressureClass: cat.pressure_class_type_name || "",
              sealClass: cat.seal_class_name || "",
              wrap: cat.wrap_name || "",
              liner: cat.liner_name || "",
              notes: cat.notes || "",
            };
            return acc;
          },
          {}
        );

        setCategoryIds(categoryIds);
        setCategories(categoriesData);
        setNextCategoryId(categoryIds.length + 1);
        setActiveTab(1);
      }
    } else if (!editingSystem && isOpen) {
      // Reset form for new system
      setSystemTitle("");
      setCategoryIds([1]);
      setNextCategoryId(2);
      setActiveTab(1);
      setCategories({
        1: {
          title: "",
          pressureClass: "",
          sealClass: "",
          wrap: "",
          liner: "",
          notes: "",
        },
      });
    }
  }, [editingSystem, isOpen]);

  if (!isOpen) return null;

  const handleCategoryInputChange = (categoryNum, field, value) => {
    setCategories((prev) => ({
      ...prev,
      [categoryNum]: {
        ...prev[categoryNum],
        [field]: value,
      },
    }));
  };

  const addCategory = () => {
    if (categoryIds.length < 3) {
      const newId = nextCategoryId;
      setCategoryIds([...categoryIds, newId]);
      setCategories((prev) => ({
        ...prev,
        [newId]: {
          title: "",
          pressureClass: "",
          sealClass: "",
          wrap: "",
          liner: "",
          notes: "",
        },
      }));
      setNextCategoryId(newId + 1);
      setActiveTab(newId); // Focus on the new category
    }
  };

  const removeCategory = (categoryId) => {
    if (categoryIds.length > 1) {
      const newCategoryIds = categoryIds.filter((id) => id !== categoryId);
      setCategoryIds(newCategoryIds);

      // Remove the category data
      const newCategories = { ...categories };
      delete newCategories[categoryId];
      setCategories(newCategories);

      // If we're on the removed tab, switch to the first available tab
      if (activeTab === categoryId) {
        setActiveTab(newCategoryIds[0]);
      }
    }
  };

  const prepareSubmitData = () => {
    // Validate required fields
    if (!systemTitle.trim()) {
      alert("Please enter a system title");
      return null;
    }

    // Prepare data to submit - only include existing categories
    const categoriesToSubmit = {};
    categoryIds.forEach((id) => {
      categoriesToSubmit[id] = categories[id];
    });

    return {
      systemTitle,
      numCategories: categoryIds.length,
      categories: categoriesToSubmit,
    };
  };

  const resetForm = () => {
    setSystemTitle("");
    setCategoryIds([1]);
    setNextCategoryId(2);
    setActiveTab(1);
    setCategories({
      1: {
        title: "",
        pressureClass: "",
        sealClass: "",
        wrap: "",
        liner: "",
        notes: "",
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const submitData = prepareSubmitData();
    if (!submitData) return;

    // Call the onSubmit callback with the form data
    onSubmit(submitData);

    // Reset form and close modal
    resetForm();
    onClose();
  };

  const handleSaveAndContinue = () => {
    const submitData = prepareSubmitData();
    if (!submitData) return;

    // Call the callback with data, parent will handle saving and opening template modal
    if (onSaveAndContinue) {
      onSaveAndContinue(submitData);
    }

    // Reset form for next system but don't close modal yet - parent will handle that
    resetForm();
  };

  const handleClose = () => {
    // Reset form on close
    setSystemTitle("");
    setCategoryIds([1]);
    setNextCategoryId(2);
    setActiveTab(1);
    setCategories({
      1: {
        title: "",
        pressureClass: "",
        sealClass: "",
        wrap: "",
        liner: "",
        notes: "",
      },
    });
    onClose();
  };

  const renderCategoryFields = (categoryNum) => {
    const categoryData = categories[categoryNum];

    return (
      <div className="grid grid-cols-2 gap-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Title
          </label>
          <input
            type="text"
            value={categoryData.title}
            onChange={(e) =>
              handleCategoryInputChange(categoryNum, "title", e.target.value)
            }
            placeholder=""
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Liner */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Liner
          </label>
          <select
            value={categoryData.liner}
            onChange={(e) =>
              handleCategoryInputChange(categoryNum, "liner", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select...</option>
            {ductSystemDropdownData.liner.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Pressure Class */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Pressure Class
          </label>
          <select
            value={categoryData.pressureClass}
            onChange={(e) =>
              handleCategoryInputChange(
                categoryNum,
                "pressureClass",
                e.target.value
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select...</option>
            {ductSystemDropdownData.pressureClass.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Wrap */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Wrap
          </label>
          <select
            value={categoryData.wrap}
            onChange={(e) =>
              handleCategoryInputChange(categoryNum, "wrap", e.target.value)
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select...</option>
            {ductSystemDropdownData.wrap.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Seal Class */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seal Class
          </label>
          <select
            value={categoryData.sealClass}
            onChange={(e) =>
              handleCategoryInputChange(
                categoryNum,
                "sealClass",
                e.target.value
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Select...</option>
            {ductSystemDropdownData.sealClass.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            value={categoryData.notes}
            onChange={(e) =>
              handleCategoryInputChange(categoryNum, "notes", e.target.value)
            }
            placeholder=""
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingSystem ? "Edit System" : "Add New System"}
          </h3>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleSaveAndContinue}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              New System
            </button>
            <button
              type="submit"
              form="duct-system-form"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
            >
              Save and Close
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Form */}
        <form
          id="duct-system-form"
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          {/* Fixed Fields Section */}
          <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
            {/* System Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                System Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={systemTitle}
                onChange={(e) => setSystemTitle(e.target.value)}
                placeholder=""
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          {/* Category Tabs and Content */}
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Tabs */}
            <div className="flex items-center border-b px-4 pt-5 flex-shrink-0">
              <div className="flex flex-1 overflow-x-auto">
                {categoryIds.map((catId) => {
                  const categoryTitle = categories[catId]?.title;
                  const displayName = categoryTitle || `Category ${catId}`;

                  return (
                    <button
                      key={catId}
                      type="button"
                      onClick={() => setActiveTab(catId)}
                      className={`relative px-4 py-2 pr-6 pt-3 font-medium text-sm border-b-2 transition-colors mr-2 ${
                        activeTab === catId
                          ? "border-indigo-600 text-indigo-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {displayName}
                      {categoryIds.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeCategory(catId);
                          }}
                          className="absolute top-0 right-0 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      )}
                    </button>
                  );
                })}

                {categoryIds.length < 3 && (
                  <button
                    type="button"
                    onClick={addCategory}
                    className="px-4 py-2 pt-3 font-medium text-sm border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition-colors mr-2"
                  >
                    Add Category
                  </button>
                )}
              </div>
            </div>

            {/* Tab Content */}
            <div className="px-4 py-3 overflow-y-auto flex-1">
              {renderCategoryFields(activeTab)}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
